import { useState, useEffect } from "react";

const API = process.env.REACT_APP_API_URL || "";

const riskColor = (level) => ({
  extreme: "#E24B4A",
  high: "#BA7517",
  moderate: "#185FA5",
  low: "#1D9E75"
}[level] || "#6B7280");

const riskBg = (level) => ({
  extreme: "#E24B4A22",
  high: "#BA751722",
  moderate: "#185FA522",
  low: "#1D9E7522"
}[level] || "#6B728022");

export default function IntelligenceMarkets() {
  const [markets, setMarkets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [sortKey, setSortKey] = useState("queue_delay_score");
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetch(`${API}/intelligence/markets`)
      .then(r => r.json())
      .then(d => { setMarkets(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const filtered = markets
    .filter(m => filter === "all" || m.risk_level === filter)
    .filter(m => !search || m.iso_region?.toLowerCase().includes(search.toLowerCase())
                          || m.geo_state?.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => (b[sortKey] ?? -1) - (a[sortKey] ?? -1));

  const counts = {
    extreme: markets.filter(m => m.risk_level === "extreme").length,
    high: markets.filter(m => m.risk_level === "high").length,
    moderate: markets.filter(m => m.risk_level === "moderate").length,
    low: markets.filter(m => m.risk_level === "low").length,
  };

  if (loading) return <div style={{ padding: 48, color: "#9a99a2" }}>Loading markets...</div>;

  return (
    <div style={{ minHeight: "100vh", background: "#0d0d1a", color: "#e2e0d8",
                  fontFamily: "'Inter', sans-serif", padding: "24px 32px" }}>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 11, color: "#5f5e6e", letterSpacing: 2,
                      textTransform: "uppercase", marginBottom: 4 }}>
          Intelligence Data
        </div>
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: "#e8e6de" }}>
          National Markets
        </h1>
        <div style={{ fontSize: 12, color: "#5f5e6e", marginTop: 4 }}>
          {markets.length} ISO markets · LBNL Queued-Up 2024 · {counts.extreme} extreme · {counts.high} high
        </div>
      </div>

      {/* Summary pills */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {["all", "extreme", "high", "moderate", "low"].map(level => (
          <button key={level} onClick={() => setFilter(level)} style={{
            padding: "5px 14px", borderRadius: 20, fontSize: 11, cursor: "pointer",
            border: filter === level
              ? `1px solid ${riskColor(level) || "#185FA5"}`
              : "1px solid #2a2a3e",
            background: filter === level
              ? (riskBg(level) || "#185FA522")
              : "#1a1a2e",
            color: filter === level
              ? (riskColor(level) || "#185FA5")
              : "#9a99a2",
          }}>
            {level === "all" ? `All (${markets.length})` : `${level} (${counts[level] || 0})`}
          </button>
        ))}
        <input
          placeholder="Search ISO or state..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          onClick={e => e.stopPropagation()}
          style={{
            padding: "5px 12px", borderRadius: 20, fontSize: 11,
            border: "1px solid #2a2a3e", background: "#1a1a2e",
            color: "#e2e0d8", outline: "none", marginLeft: "auto"
          }}
        />
      </div>

      {/* Table */}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ background: "#1a1a2e", color: "#5f5e6e" }}>
              {[
                ["#", null],
                ["ISO Region", null],
                ["State", null],
                ["Queue Score", "queue_delay_score"],
                ["Risk Level", null],
                ["Total Projects", "total_projects"],
                ["Withdrawn", "withdrawn_count"],
                ["W/D Rate %", "withdrawal_rate"],
                ["Avg MW", "avg_capacity_mw"],
              ].map(([label, key]) => (
                <th
                  key={label}
                  onClick={() => key && setSortKey(key)}
                  style={{
                    padding: "8px 12px", textAlign: "left",
                    borderBottom: "1px solid #2a2a3e",
                    cursor: key ? "pointer" : "default",
                    color: sortKey === key ? "#185FA5" : "#5f5e6e",
                    whiteSpace: "nowrap",
                    userSelect: "none",
                  }}
                >
                  {label} {sortKey === key ? "\u2193" : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((m, i) => (
              <tr key={`${m.iso_region}-${m.geo_state}`}
                style={{ borderBottom: "1px solid #1a1a2e",
                         background: i % 2 === 0 ? "transparent" : "#0f0f1a" }}>
                <td style={{ padding: "6px 12px", color: "#5f5e6e" }}>{i + 1}</td>
                <td style={{ padding: "6px 12px", fontWeight: 600, color: "#e8e6de" }}>
                  {m.iso_region}
                </td>
                <td style={{ padding: "6px 12px", color: "#9a99a2" }}>{m.geo_state}</td>
                <td style={{ padding: "6px 12px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 700,
                                   color: riskColor(m.risk_level) }}>
                      {m.queue_delay_score != null ? Math.round(m.queue_delay_score) : "\u2014"}
                    </span>
                    <div style={{
                      flex: 1, height: 4, background: "#2a2a3e", borderRadius: 2,
                      overflow: "hidden", minWidth: 60
                    }}>
                      <div style={{
                        height: "100%", borderRadius: 2,
                        width: `${m.queue_delay_score || 0}%`,
                        background: riskColor(m.risk_level)
                      }} />
                    </div>
                  </div>
                </td>
                <td style={{ padding: "6px 12px" }}>
                  <span style={{
                    fontSize: 10, color: riskColor(m.risk_level),
                    background: riskBg(m.risk_level),
                    padding: "2px 8px", borderRadius: 10,
                    textTransform: "uppercase", fontWeight: 600
                  }}>
                    {m.risk_level}
                  </span>
                </td>
                <td style={{ padding: "6px 12px", color: "#9a99a2" }}>
                  {m.total_projects?.toLocaleString() || "\u2014"}
                </td>
                <td style={{ padding: "6px 12px", color: "#9a99a2" }}>
                  {m.withdrawn_count?.toLocaleString() || "\u2014"}
                </td>
                <td style={{ padding: "6px 12px", color: "#9a99a2" }}>
                  {m.withdrawal_rate != null ? `${m.withdrawal_rate.toFixed(1)}%` : "\u2014"}
                </td>
                <td style={{ padding: "6px 12px", color: "#9a99a2" }}>
                  {m.avg_capacity_mw != null ? `${m.avg_capacity_mw.toFixed(1)} MW` : "\u2014"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
